/*
 * Copyright (c) 2020 JCA Electronics , Winnipeg, MB. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { LayerType } from '@/flightpath-components/models/enums/LayerType';
import { AlarmModel } from '@/flightpath-components/models/AlarmModel';
import { Group } from '@/flightpath-components/models/CognitoUser';
import { GridLocation } from '@/flightpath-components/models/GridLocation';
import { Convert } from '@/flightpath-components/models/MissionPlan';
import { Point } from '@/flightpath-components/models/Point';
import { RasterModel } from '@/flightpath-components/models/RasterModel';
import Auth from '@aws-amplify/auth';
import { AxiosInstance, AxiosResponse } from 'axios';
import { injectable } from 'inversify';
import { Convert as AlarmConvert } from '@/flightpath-components/models/AlarmModel';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { FlightPathAxios } from './api';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { ImplementOperationsModel } from '@/flightpath-components/models/implement/ImplementOperationsModel';
import { ApplicationDataForMachine } from '@/flightpath-components/models/ApplicationDataForMachine';
import { DeployedSinglePathMissionQuery } from '@/flightpath-components/models/DeployedSinglePathMissionsQuery';
import { MultiMachineMission } from '@/flightpath-components/models/MultiMachineMission';
import { DeployedMultiMachineMission } from '@/flightpath-components/models/DeployedMultiMachineMission';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import {
  SimplePathPathPlanningInput,
  MultiMachineMissionPathPlanningInput,
  PathPlanningRequest
} from '@/flightpath-components/models/PathPlanningInput';
import { DeployedLeaderFollowerMission } from '@/flightpath-components/models/LeaderFollower/DeployedLeaderFollowerMission';
import { Farm } from '@/flightpath-components/models/LeaderFollower/Farm';
import { FarmField } from '@/flightpath-components/models/LeaderFollower/FarmField';
import { FarmObstacle } from '@/flightpath-components/models/LeaderFollower/FarmObstacle';
import { LeaderFollowerMission } from '@/flightpath-components/models/LeaderFollower/LeaderFollowerMission';
import { ProductPrescription } from '@/flightpath-components/models/Prescriptions';
import { PrecisionAgSimpleJobData } from '@/flightpath-components/models/precisionAg/PrecisionAgSimpleJobData';

@injectable()
export class FlightPathService implements FlightPathServiceInterface {
  accessToken: string;
  apiService: FlightPathAxios;
  instance: AxiosInstance;
  constructor() {
    this.accessToken = '';
    this.apiService = new FlightPathAxios();
    this.instance = this.apiService.getInstance();
  }
  getAllImplementsMultiBoom(id: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getColorLegend(machineKey: MachineKey, jobId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getLayerReport(accountId: string, jobId: string, layerType: number): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getMapTile(accountId: string, missionId: string, layerType: number): Promise<AxiosResponse<any>> {
    return this.instance.get(`/render/raster-standard-missionid/${accountId}`, {
      params: {
        MissionDeploymentID: missionId,
        Type: layerType
      },
      responseType: 'json'
    });
  }
  async getRasterModelMission(mission: DeployedSinglePathMission, type: number): Promise<RasterModel | undefined> {
    try {
      const response = await this.getMapTile(mission.CustomerID, mission.MissionDeploymentID, type);
      const jsonObject = response.data;
      const rasterStandardModel = Convert.toRasterStandardModel(JSON.stringify(jsonObject));

      const stream = Uint8Array.from(atob(rasterStandardModel.Base64StringImage), (c) => c.charCodeAt(0));
      const GridX = rasterStandardModel.GridX;
      const GridY = rasterStandardModel.GridY;
      const responseLayerType = rasterStandardModel.Type as LayerType;
      const NorthEast: Point = {
        Item1: rasterStandardModel.NorthEastLatitude,
        Item2: rasterStandardModel.NorthEastLongitude
      };
      const SouthWest: Point = {
        Item1: rasterStandardModel.SouthWestLatitude,
        Item2: rasterStandardModel.SouthWestLongitude
      };

      if (responseLayerType == 0) return undefined;
      const rasterModel: RasterModel = {
        ImageStream: stream,
        GridX: GridX,
        GridY: GridY,
        Type: responseLayerType,
        NorthEast: NorthEast,
        SouthWest: SouthWest,
        MachineKey: { machineId: mission.TargetMachine.machineId },
        ErrorNotes: '',
        TimeStamp: Date.now()
      };
      return rasterModel;
    } catch (err) {
      console.info(`Map Tile for Layer ${type} Not Found`);
      return undefined;
    }
  }

  getStitchedRasterForJobIdLayerType(jodId: string, layerType: number): Promise<RasterModel> {
    throw new Error('Method not implemented.');
  }
  getFarms(accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getFarmById(farmId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  updateFarm(farm: Farm, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteFarm(farmId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getFarmFields(farmId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getFarmFieldById(fieldId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  updateFarmField(field: FarmField, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteFarmField(fieldId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getFarmObstacles(farmId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getFarmObstacleById(obstacleId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  updateFarmObstacle(field: FarmObstacle, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteFarmObstacle(obstacleId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getLeaderFollowerMissionsByCustomer(accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getLeaderFollowerMissionById(missionId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  updateLeaderFollowerMission(mission: LeaderFollowerMission): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteLeaderFollowerMission(missionId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deployLeaderFollowerMission(mission: DeployedLeaderFollowerMission, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  cancelDeployedLeaderFollowerMission(mission: DeployedLeaderFollowerMission, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getActiveDeployedLeaderFollowerMissions(customerId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  planLeaderFollowerMissionPath(pathPlanningInput: MultiMachineMissionPathPlanningInput): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getPathPlan(pathPlanningInput: PathPlanningRequest): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  exportShapefile(jobId: string, jobName: string, accountId: string, machineId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  exportMultiJobShapefile(jobIds: PrecisionAgSimpleJobData[], accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteShapefile(jobId: string, jobName: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  compressShapefile(jobId: string, jobName: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  importPrescriptions(prescriptions: ProductPrescription[], accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getPrescriptions(accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getLatestDeployedMultiMachineMission(fleetId: string, accountId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  planSimplePathPath(pathPlanningInput: SimplePathPathPlanningInput): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  planMultiMachineMissionPath(pathPlanningInput: MultiMachineMissionPathPlanningInput): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getAllRenderedImplements(): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getLatestRenderedImplement(machineKey: MachineKey): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deployMutliMachineMission(mission: MultiMachineMission, customerId: string, machineId: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getActiveDeployedMutliMachineMissionByMissionId(id: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  insertOrUpdateMultiMachineMission(mission: MultiMachineMission): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getMultiMachineMissionsByCustomerId(id: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  getMultiMachineMissionByMissionId(id: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
  deleteMultiMachineMissionByMissionId(id: string): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }

  async updateToken(): Promise<void> {
    this.accessToken = await this.getUpdateToken();
  }
  async getUpdateToken(): Promise<string> {
    return `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`;
  }
  async getUser(email: string): Promise<AxiosResponse<any>> {
    return this.instance.get('/umc/user', {
      params: {
        userName: email
      },
      responseType: 'json'
    });
  }
  async addUser(email: string, group: string): Promise<AxiosResponse<any>> {
    const json = {
      userName: email,
      groupName: group
    };

    return this.instance.post('/umc/user', json);
  }

  async updateUser(userName: string, groups: Group[]): Promise<AxiosResponse<any>> {
    const json = {
      userName: userName,
      Groups: groups
    };
    
    return this.instance.put('/umc/user', json);
  }
  async deleteUser(email: string): Promise<AxiosResponse<any>> {
    return this.instance.delete('/umc/user', {
      params: {
        userName: email
      },
      responseType: 'json'
    });
  }
  async enableUser(email: string): Promise<AxiosResponse<any>> {
    return this.instance.put(`/umc/user/enable?userName=${email}`);
  }

  async disableUser(email: string): Promise<AxiosResponse<any>> {
    return this.instance.put(`/umc/user/disable?userName=${email}`);
  }

  async inviteUser(email: string): Promise<AxiosResponse<any>> {
    return this.instance.put(`/umc/user/reinvite?userName=${encodeURIComponent(email)}`);
  }

  async getAllUsers(limit: number, token: string, filter: string): Promise<AxiosResponse<any>> {
    return this.instance.get('/umc/users', {
      params: {
        limit: limit,
        paginationToken: token
      },
      responseType: 'json'
    });
  }
  async getAllUsersGroup(limit: number, token: string, group: string): Promise<AxiosResponse<any>> {
    return this.instance.get('/umc/users/group', {
      params: {
        limit: limit,
        token: token,
        groupName: group
      },
      responseType: 'json'
    });
  }
  getAvailableMachines(): Promise<AxiosResponse<any>> {
    return this.instance.get('/general/available-machines');
  }

  getMachineStatus(machines: Array<string>): Promise<AxiosResponse<any>> {
    return this.instance.post('/general/machine-statuses', machines);
  }

  updateMap(id?: string): Promise<AxiosResponse<any>> {
    return id != undefined ? this.instance.get(`/render/update-map?JobID=${id}`) : this.instance.get(`/render/update-map`);
  }

  setAlarmTable(table: AlarmModel[]): Promise<AxiosResponse<any>> {
    return this.instance.post(`/alarm/table`, table);
  }

  getAlarmTable(userName: string, machines: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/alarm/table?userName=${userName}&machines=${machines}`);
  }

  acknowledgeAlarm(request: AlarmModel[], user: string): Promise<AxiosResponse<any>> {
    request.forEach((alarm) => {
      alarm.UserName = user;
    });
    const convertedRequest = AlarmConvert.toFlightPathAlarmModelList(request);
    return this.instance.put(`/alarm/acknowledge`, convertedRequest);
  }

  /**
   * Gets Json containing Raster, Grid Location, Layer of requested grid.
   * @param grid
   * @param type
   * @param machineKeys
   */
  private getRaster(grid: GridLocation, type: number, machineKeys: string[]): Promise<AxiosResponse<any>> {
    return this.instance.get(`/render/raster-standard`, {
      params: {
        GridX: grid.X,
        GridY: grid.Y,
        Type: type,
        MachineKeyList: machineKeys.join(',')
      },
      responseType: 'json'
    });
  }

  /**
   * Gets Json containing Raster, Grid Location, Layer of requested grid.
   * @param grid
   * @param type
   * @param machineKeys
   */
  private getRasterByJob(grid: GridLocation, type: number, machineKey: string, jobId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/render/raster-standard-jobid`, {
      params: {
        GridX: grid.X,
        GridY: grid.Y,
        Type: type,
        MachineID: machineKey,
        JobID: jobId
      },
      responseType: 'json'
    });
  }

  /**
   * Transforms RasterJsonModel into Raster for Map Rendering
   * @param grid
   * @param type
   * @param machineKey
   * @param timestamp
   */
  async getRasterModel(grid: GridLocation, type: number, machineKey: string, timestamp: number): Promise<RasterModel> {
    const response = await this.getRaster(grid, type, [machineKey]);
    const jsonObject = response.data;
    const rasterStandardModel = Convert.toRasterStandardModel(JSON.stringify(jsonObject));

    const stream = Uint8Array.from(atob(rasterStandardModel.Base64StringImage), (c) => c.charCodeAt(0));
    const GridX = rasterStandardModel.GridX;
    const GridY = rasterStandardModel.GridY;
    const responseLayerType = rasterStandardModel.Type as LayerType;
    const NorthEast: Point = {
      Item1: rasterStandardModel.NorthEastLatitude,
      Item2: rasterStandardModel.NorthEastLongitude
    };
    const SouthWest: Point = {
      Item1: rasterStandardModel.SouthWestLatitude,
      Item2: rasterStandardModel.SouthWestLongitude
    };

    const rasterModel: RasterModel = {
      ImageStream: stream,
      GridX: GridX,
      GridY: GridY,
      Type: responseLayerType,
      NorthEast: NorthEast,
      SouthWest: SouthWest,
      MachineKey: { machineId: machineKey },
      ErrorNotes: '',
      TimeStamp: timestamp
    };
    return rasterModel;
  }

  /**
   * Transforms RasterJsonModel into Raster for Map Rendering
   * @param grid
   * @param type
   * @param machineKey
   * @param timestamp
   */
  async getRasterModelJobId(grid: GridLocation, type: number, machineKey: string, jobId: string, timestamp: number): Promise<RasterModel> {
    const response = await this.getRasterByJob(grid, type, machineKey, jobId);
    const jsonObject = response.data;
    const rasterStandardModel = Convert.toRasterStandardModel(JSON.stringify(jsonObject));

    const stream = Uint8Array.from(atob(rasterStandardModel.Base64StringImage), (c) => c.charCodeAt(0));
    const GridX = rasterStandardModel.GridX;
    const GridY = rasterStandardModel.GridY;
    const responseLayerType = rasterStandardModel.Type as LayerType;
    const NorthEast: Point = {
      Item1: rasterStandardModel.NorthEastLatitude,
      Item2: rasterStandardModel.NorthEastLongitude
    };
    const SouthWest: Point = {
      Item1: rasterStandardModel.SouthWestLatitude,
      Item2: rasterStandardModel.SouthWestLongitude
    };

    const rasterModel: RasterModel = {
      ImageStream: stream,
      GridX: GridX,
      GridY: GridY,
      Type: responseLayerType,
      NorthEast: NorthEast,
      SouthWest: SouthWest,
      MachineKey: { machineId: machineKey },
      ErrorNotes: '',
      TimeStamp: timestamp
    };
    return rasterModel;
  }

  getPathsByCustomerId(id: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/simplepaths/summary/customerid/${id}`);
  }

  getPathByPathId(id: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/simplepaths/pathid/${accountId}?PathID=${id}`);
  }

  getPathByFieldId(id: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/simplepaths/fieldid/${accountId}?FieldID=${id}`);
  }

  deletePathByPathId(id: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.delete(`/simplepaths/${accountId}?PathID=${id}`);
  }

  insertOrUpdatePath(path: SimplePath): Promise<AxiosResponse<any>> {
    return this.instance.post('/simplepaths', path.fromModelToJson());
  }

  getAvailableFields(customerId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/fields/field-objects/${customerId}`);
  }

  getAvailableFieldNames(customerId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/fields/${customerId}`);
  }

  addFieldDefinition(field: FieldOperationsModel): Promise<AxiosResponse<any>> {
    return this.instance.post('/fields', field.fromModelToJson());
  }

  deleteFieldDefinition(fieldId: string): Promise<AxiosResponse<any>> {
    return this.instance.delete(`/fields?FieldID=${fieldId}`);
  }

  getFieldDefinition(fieldId: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/fields/definition/${accountId}?FieldID=${fieldId}`);
  }

  insertOrUpdateMission(mission: SinglePathMission): Promise<AxiosResponse<any>> {
    return this.instance.post('/singlepathmissions', mission.fromModelToJson());
  }

  getMissionsByCustomerId(id: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/singlepathmissions/summary/customerid/${id}`);
  }

  getMissionsListByCustomerId(id: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/singlepathmissions/customerid-list/${id}?`);
  }

  getMissionByMissionId(id: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/singlepathmissions/missionid/${accountId}?MissionID=${id}`);
  }

  deleteMissionByMissionId(id: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.delete(`/singlepathmissions/${accountId}?MissionID=${id}`);
  }

  getMissionStatus(machineId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/jobs/mission-status-auto-transition?MachineID=${machineId}`);
  }

  deploySinglePathMission(mission: DeployedSinglePathMission, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.post(`/jobs/deployed-single-path-mission/${accountId}`, mission.fromModelToJson());
  }

  getAllImplements(id: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/implements?AccountId=${id}`);
  }

  addImplement(implement: ImplementOperationsModel, id: string): Promise<AxiosResponse<any>> {
    return this.instance.post(`/implements?AccountId=${id}`, implement.fromModelToJson());
  }

  deleteImplement(id: string): Promise<AxiosResponse<any>> {
    return this.instance.delete(`/implements?ImplementId=${id}`);
  }

  setApplicationCloudCache(payload: ApplicationDataForMachine): Promise<AxiosResponse<any>> {
    return this.instance.post('/machine-data/application-cloud-cache', payload);
  }

  cancelDeployedSinglePathMission(mission: DeployedSinglePathMission, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.post(`/jobs/cancel-deployed-single-path-mission/${accountId}`, mission.fromModelToJson());
  }
  getActiveDeployedSinglePathMissions(customerId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/jobs/active-single-path-missions-list/${customerId}`);
  }

  queryCompletedMission(query: DeployedSinglePathMissionQuery): Promise<AxiosResponse<any>> {
    return this.instance.post('/jobs/completed-deployed-single-path-mission', query);
  }

  getEntityLastModified(id: string, entities: string[]): Promise<AxiosResponse<any>> {
    return this.instance.get(`/general/last-modified?CustomerID=${id}&Entities=${entities.join(',')}`);
  }

  editActiveDeployedMutliMachineMission(missionId: string, editedMission: DeployedMultiMachineMission): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }

  queryCompletedMissionsSummary(query: DeployedSinglePathMissionQuery): Promise<AxiosResponse<any>> {
    return this.instance.post('/jobs/completed-deployed-single-path-mission/summary', query);
  }

  getDeployedSinglePathMission(missionDeploymentID: string, accountId: string): Promise<AxiosResponse<any>> {
    return this.instance.get(`/jobs/deployed-single-path-mission/${accountId}?MissionDeploymentID=${missionDeploymentID}`);
  }
  getAvailableFleets(): Promise<AxiosResponse<any>> {
    throw new Error('Method not implemented.');
  }
}
